import * as React from 'react';
import { Skeleton } from '@chess/api-skeleton';
import { Pie, PieLegend, TextSkeleton, type CardProps, type FilterLink, type PieSlice } from '@chess/api-insights/charts';
import { ENDING_RESULT_VIEWS, endingCellText } from '@chess/api-ending-case';
import '@chess/api-ending-case/colors.css';
import { CardAside, CardEmpty, CardNote } from '@chess/api-dashboard/react';
import { endingsOf, type EndingResultsResult } from './EndingResultsAggregator.ts';
import './ending-results.css';

// Ending results: every scanned game of the period that reached an ending,
// in one of five slices from how the ending started (winning at the Winning
// setting or more, lost at minus it or less, level between) and how the
// game ended: Won, Comeback (won or drew from a lost start), Drawn (from a
// level start), Blown win (drew or lost from a winning start), Lost. A
// slice's tooltip names its start × outcome cells. Every slice and legend
// entry opens exactly those games in the library (`ending-result`). The note
// gives the scope: how many of the period's games reached an ending. Drawn
// from props only.

export type EndingResultsCardProps = CardProps<{ result: EndingResultsResult }, {
  /** Opens the library filtered to a slice's games. */
  link?: FilterLink;
}>;

const SIZE = 96;
const games = (n: number) => `${n} ${n === 1 ? 'game' : 'games'}`;

function slicesOf(r: EndingResultsResult): PieSlice[] {
  return ENDING_RESULT_VIEWS.map((v) => {
    const cells = r.cells[v.result].map((c) => `${endingCellText(c.start, c.outcome)} ${c.count}`).join(' · ');
    return {
      key: v.result,
      label: v.label,
      count: r.byResult[v.result],
      colour: v.colour,
      title: `${v.label}: ${cells || v.description} · opens those games`,
      filter: { endingResult: v.result },
    };
  });
}

const LoadingBody: React.FC = () => (
  <div className="donut-wrap">
    <Skeleton loading variant="circular" width={`${SIZE / 16}rem`} height={`${SIZE / 16}rem`} label="Loading your ending results" />
    <ul className="legend">
      {ENDING_RESULT_VIEWS.map((v) => (
        <li key={v.result}>
          <span className="legend-swatch" style={{ background: v.colour }} />
          {v.label}
          <span className="legend-value">
            <Skeleton loading inline variant="text" shape="12 · 34%" />
          </span>
        </li>
      ))}
    </ul>
  </div>
);

export const TITLE = 'Ending results';
const ASIDE = 'from the start of the ending';

export const EndingResultsCard: React.FC<EndingResultsCardProps> = (props) => {
  if (props.loading)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <CardNote>
          <TextSkeleton sample="18 of 188 games reached an ending" />
        </CardNote>
        <div className="ending-results-body">
          <LoadingBody />
        </div>
      </>
    );
  const { result, link } = props;
  const endings = endingsOf(result);
  if (!endings)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <CardEmpty>No endings in the last 30 days.</CardEmpty>
      </>
    );
  const slices = slicesOf(result);
  return (
    <>
      <CardAside>{ASIDE}</CardAside>
      <CardNote>{`${endings} of ${games(result.games)} reached an ending`}</CardNote>
      <div className="ending-results-body">
        <div className="donut-wrap">
          <Pie slices={slices} size={SIZE} link={link} />
          <PieLegend slices={slices} link={link} />
        </div>
      </div>
    </>
  );
};
